/**
 ⚠️ Design System — Protected file
 Do not edit this file without prior approval from the Design System team.
 */

import React, { FunctionComponent } from 'react'
import styled from 'styled-components/native'

import { AccessibilityRole } from 'libs/accessibilityRole/accessibilityRole'
import { clamp } from 'ui/designSystem/ProgressBar/helpers/clamp'
import { computeProgressRatio } from 'ui/designSystem/ProgressBar/helpers/computeProgressRatio'
import { getProgressText } from 'ui/designSystem/ProgressBar/helpers/getProgressText'
import { Typo } from 'ui/theme'

import { ProgressBarProps } from './types'

const DEFAULT_MAX_VALUE = 100
const MIN_VALUE = 0

export const ProgressBar: FunctionComponent<ProgressBarProps> = ({
  value,
  max = DEFAULT_MAX_VALUE,
  type = 'percentage',
  labelPosition = false,
  accessibilityLabel,
  progressColor,
  ...props
}) => {
  const normalizedMax = Math.max(max, MIN_VALUE)
  const progressRatio = computeProgressRatio(value, normalizedMax)
  const progressText = getProgressText(type, value, normalizedMax)
  const accessibilityValueText =
    type === 'steps' ? `${clamp(value, normalizedMax)} sur ${normalizedMax}` : progressText

  return (
    <Container
      labelPosition={labelPosition}
      accessibilityRole={AccessibilityRole.PROGRESSBAR}
      accessibilityLabel={accessibilityLabel}
      accessibilityValue={{
        min: MIN_VALUE,
        max: normalizedMax,
        now: clamp(value, normalizedMax),
        text: accessibilityValueText,
      }}
      accessible
      {...props}>
      <BarAndRightLabelContainer labelPosition={labelPosition}>
        <BarTrack testID="progress-bar-track">
          <BarProgress
            progressRatio={progressRatio}
            progressColor={progressColor}
            testID="progress-bar-progress"
          />
        </BarTrack>
        {labelPosition === 'right' ? (
          <ProgressText testID="progress-bar-label">{progressText}</ProgressText>
        ) : null}
      </BarAndRightLabelContainer>
      {labelPosition === 'bottom' ? (
        <ProgressText testID="progress-bar-label">{progressText}</ProgressText>
      ) : null}
    </Container>
  )
}

const Container = styled.View<Pick<ProgressBarProps, 'labelPosition'>>(
  ({ theme, labelPosition }) => ({
    width: '100%',
    gap: labelPosition === 'bottom' ? theme.designSystem.size.spacing.xs : undefined,
  })
)

const BarAndRightLabelContainer = styled.View<Pick<ProgressBarProps, 'labelPosition'>>(
  ({ theme, labelPosition }) => ({
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: labelPosition === 'right' ? theme.designSystem.size.spacing.m : undefined,
  })
)

const BarTrack = styled.View(({ theme }) => ({
  flex: 1,
  height: theme.designSystem.size.spacing.s,
  overflow: 'hidden',
  borderWidth: 1,
  borderColor: theme.designSystem.color.border.default,
  borderRadius: theme.designSystem.size.borderRadius.pill,
  backgroundColor: theme.designSystem.color.background.subtle,
}))

const BarProgress = styled.View<{ progressRatio: number; progressColor?: string }>(
  ({ theme, progressRatio, progressColor }) => ({
    height: '100%',
    width: `${progressRatio}%`,
    borderRadius: theme.designSystem.size.borderRadius.pill,
    backgroundColor: progressColor ?? theme.designSystem.color.background.brandPrimary,
  })
)

const ProgressText = styled(Typo.BodyS)(({ theme }) => ({
  color: theme.designSystem.color.text.default,
  flexShrink: 0,
}))
